import React, { Fragment, ReactNode } from "react";
import { colors2, UI_TITLE, UI_TITLE_SM } from "oolib";
import {
  StyledBreadcrumbHeader,
  StyledCrumb,
  StyledCrumbButton,
  StyledCrumbLink,
  StyledNodeCrumb,
  StyledSeparator,
} from "./styled";

const TYPO = {
  UI_TITLE,
  UI_TITLE_SM,
};

/** Share of the header the current (last) crumb may occupy; ancestors split the rest. */
const CURRENT_CRUMB_MAX_WIDTH = 60;

export interface BreadcrumbHeaderItem {
  /** Text of the crumb. Ignored when `node` is passed. */
  display?: ReactNode;
  /** Renders the crumb as a router link. */
  to?: string;
  /** Renders the crumb as clickable text (for back-nav that isn't a route). */
  onClick?: () => void;
  /**
   * Escape hatch for a crumb that isn't text — e.g. an inline editable title.
   * Gets the same width cap, but keeps its own typography and handles its own
   * truncation.
   */
  node?: ReactNode;
  key?: string;
}

interface BreadcrumbHeaderProps {
  /** Ancestors first, current page last. Falsy entries are dropped. */
  items: (BreadcrumbHeaderItem | false | null | undefined)[];
  typo?: keyof typeof TYPO;
  /** Colour of the last (current) crumb, and of the separators by default. */
  color?: string;
  /** Colour of every crumb before the last. */
  mutedColor?: string;
  separatorColor?: string;
  separator?: string;
  fontWeight?: number;
  className?: string;
  style?: React.CSSProperties;
}

/**
 * `Parent / Current` page header, e.g. "Datasets / Consultation Meeting Reports".
 *
 * Never wraps: the current crumb is capped at 60% of the header and the
 * ancestors split the remaining 40%, and anything longer truncates with a
 * native tooltip carrying the full text.
 *
 * Truncating needs every flex ancestor up the chain to permit shrinking
 * (`min-width: 0`), so parents of this comp usually need that too.
 *
 * Typography sits on each text crumb rather than the wrapper, so a `node` crumb
 * (the reports header passes an editable title) isn't restyled by it.
 *
 * Colours are props rather than hardcoded because callers theme differently —
 * PublishedPageV3 passes its CSS-var theme tokens, the rest pass colors2.
 */
export const BreadcrumbHeader = ({
  items,
  typo = "UI_TITLE",
  color = colors2.grey80,
  mutedColor = colors2.grey40,
  separatorColor,
  separator = "/",
  fontWeight,
  className,
  style,
}: BreadcrumbHeaderProps) => {
  const crumbs = items.filter(Boolean) as BreadcrumbHeaderItem[];
  const Typo = TYPO[typo];

  const ancestorCount = Math.max(crumbs.length - 1, 1);
  const ancestorMaxWidth = `${(100 - CURRENT_CRUMB_MAX_WIDTH) / ancestorCount}%`;

  const renderCrumb = (
    { display, to, onClick }: BreadcrumbHeaderItem,
    isLast: boolean
  ) => {
    // only the current crumb gets the full-strength colour
    const text = (
      <Typo style={{ color: isLast ? color : mutedColor, fontWeight }}>
        {display}
      </Typo>
    );

    if (to) return <StyledCrumbLink to={to}>{text}</StyledCrumbLink>;
    if (onClick)
      return <StyledCrumbButton onClick={onClick}>{text}</StyledCrumbButton>;
    return text;
  };

  return (
    <StyledBreadcrumbHeader className={className} style={style}>
      {crumbs.map((item, i) => {
        const isLast = i === crumbs.length - 1;
        const maxWidth = isLast
          ? `${CURRENT_CRUMB_MAX_WIDTH}%`
          : ancestorMaxWidth;
        return (
          <Fragment key={item.key || i}>
            {i > 0 && (
              <StyledSeparator>
                <Typo style={{ color: separatorColor || color, fontWeight }}>
                  {separator}
                </Typo>
              </StyledSeparator>
            )}
            {item.node ? (
              <StyledNodeCrumb $maxWidth={maxWidth}>{item.node}</StyledNodeCrumb>
            ) : (
              <StyledCrumb
                $maxWidth={maxWidth}
                // hint for the truncated case; harmless when the text fits
                title={typeof item.display === "string" ? item.display : undefined}
              >
                {renderCrumb(item, isLast)}
              </StyledCrumb>
            )}
          </Fragment>
        );
      })}
    </StyledBreadcrumbHeader>
  );
};

export default BreadcrumbHeader;
